import { useEffect, useState } from "react";
import { X, CalendarClock, Loader2, CheckCircle2, BadgeDollarSign, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const TIME_SLOTS = [
  "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00",
];

/**
 * BookExpertModal — books a 1-on-1 with a Clinq fundraising expert.
 *
 * The backend auto-detects the session type:
 *   • first session  → FREE, 30 mins
 *   • follow-ups     → 60 mins at KES 7,500, paid via Paystack before we confirm
 */
export default function BookExpertModal({ open, onClose, onBooked }) {
  const [quote, setQuote] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDone(false);
    setQuote(null);
    api.get("/expert-sessions/quote")
      .then(({ data }) => setQuote(data))
      .catch(() => toast.error("Couldn't load session details. Try again."));
  }, [open]);

  if (!open) return null;
  const today = new Date().toISOString().slice(0, 10);
  const isFree = quote?.price_kes === 0;

  const submit = async () => {
    if (!date || !time) { toast.error("Pick a date and time first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/expert-sessions/book", {
        preferred_date: date, preferred_time: time, topic,
      });
      if (data.checkout_url) {
        // Paid follow-up — hand off to Paystack; we confirm after payment.
        window.location.href = data.checkout_url;
        return;
      }
      setDone(true);
      onBooked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="book-expert-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A8B85]">Book a session</p>
            <h3 className="mt-1 text-xl font-extrabold text-neutral-900">Talk to a Clinq fundraising expert</h3>
          </div>
          <button onClick={onClose} data-testid="book-expert-close" aria-label="Close"
            className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="book-expert-success">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-2 text-sm font-bold text-emerald-900">Session requested!</p>
            <p className="mt-1 text-xs leading-relaxed text-emerald-800">
              Our team will email you to confirm the exact time for {date} at {time}.
            </p>
            <button onClick={onClose} data-testid="book-expert-done"
              className="mt-4 rounded-full bg-[#0A8B85] px-5 py-2 text-xs font-bold text-white hover:bg-[#066667]">
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Auto-detected session type banner */}
            {!quote ? (
              <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-neutral-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking your session…
              </p>
            ) : isFree ? (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="book-expert-quote-free">
                <Gift className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <p className="text-xs leading-relaxed text-emerald-900">
                  <b>Your first session is free</b> — 30 minutes, 1-on-1 with a Clinq fundraising expert. No payment needed.
                </p>
              </div>
            ) : (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="book-expert-quote-paid">
                <BadgeDollarSign className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-xs leading-relaxed text-amber-900">
                  <b>Follow-up session — 60 minutes at KES 7,500.</b> You&apos;ll be redirected to
                  Paystack to pay; we confirm your slot by email right after.
                </p>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-neutral-700" htmlFor="exs-date">Preferred date</label>
                <input id="exs-date" type="date" min={today} value={date}
                  data-testid="book-expert-date"
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-[#0A8B85] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700" htmlFor="exs-time">Preferred time (EAT)</label>
                <select id="exs-time" value={time}
                  data-testid="book-expert-time"
                  onChange={(e) => setTime(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-[#0A8B85] focus:outline-none">
                  <option value="">Select…</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-semibold text-neutral-700" htmlFor="exs-topic">What would you like to cover? (optional)</label>
              <textarea id="exs-topic" rows={3} maxLength={500} value={topic}
                data-testid="book-expert-topic"
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Reviewing my Capital Readiness Score, preparing for a bank facility, pitch feedback…"
                className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-[#0A8B85] focus:outline-none" />
            </div>

            <button onClick={submit} disabled={busy || !quote}
              data-testid="book-expert-submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
              {isFree ? "Request my free session" : "Pay KES 7,500 & book"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
